import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tabs, Tab, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { publicPassAPI } from '../../services/api';

interface QrScannerProps {
  open: boolean;
  onClose: () => void;
  onResult: (screeningId: number, token?: string, prefetched?: unknown) => void;
}

type ParsedQr = { screeningId: number; token?: string };

const parseQrResult = (text: string): ParsedQr | null => {
  const raw = text.trim();

  // Legacy deep-link format: voyagesante://traveller/{id}
  const deepLink = raw.match(/^voyagesante:\/\/traveller\/(\d+)$/i);
  if (deepLink) return { screeningId: parseInt(deepLink[1], 10) };

  // URL format: .../pass/{token}  — token is base64url("{id}:{hmac}")
  const urlMatch = raw.match(/\/pass\/([A-Za-z0-9_=-]+)/);
  if (urlMatch) {
    try {
      const token = urlMatch[1];
      const padded = token + '='.repeat((4 - token.length % 4) % 4);
      const payload = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
      const id = parseInt(payload.split(':')[0], 10);
      if (!isNaN(id) && id > 0) return { screeningId: id, token };
    } catch { /* ignore decode errors */ }
  }

  return null;
};

const SCANNER_ID = 'qr-camera-region';

const QrScanner: React.FC<QrScannerProps> = ({ open, onClose, onResult }) => {
  const [tab, setTab] = useState<0 | 1>(0); // 0=camera, 1=upload
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scanningRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handledRef = useRef(false);

  const stopCamera = async () => {
    if (scannerRef.current && scanningRef.current) {
      try { await scannerRef.current.stop(); } catch { /* ignore */ }
      scanningRef.current = false;
    }
  };

  const acceptResult = async (parsed: ParsedQr) => {
    if (handledRef.current || checking) return;
    handledRef.current = true;
    setChecking(true);
    setError(null);
    try {
      // Pass data fetched during the validity check — handed to the pass page
      // via onResult so it can render without a second round trip.
      let prefetched: unknown = undefined;
      if (parsed.token) {
        try {
          prefetched = await publicPassAPI.getPass(parsed.token);
        } catch (e: any) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          if (status === 410 || (typeof detail === 'string' && /expir/i.test(detail))) {
            setError(typeof detail === 'string' ? detail : 'This QR code has expired. The health pass is no longer valid.');
            handledRef.current = false;
            setChecking(false);
            return;
          }
          // 401/404: still open staff view for logged-in users; only block clear expiry
        }
      }
      await stopCamera();
      onResult(parsed.screeningId, parsed.token, prefetched);
      onClose();
    } finally {
      setChecking(false);
    }
  };

  const startCamera = async () => {
    setError(null);
    handledRef.current = false;
    try {
      const scanner = new Html5Qrcode(SCANNER_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          const parsed = parseQrResult(decodedText);
          if (parsed) {
            acceptResult(parsed);
          } else {
            setError('Not a valid Voyage Santé+ QR code. Scan the QR from the health pass.');
          }
        },
        () => { /* scan failures are normal, ignore */ },
      );
      scanningRef.current = true;
    } catch (err: any) {
      setError(err?.message || 'Could not access camera. Check browser permissions.');
    }
  };

  // Start/stop camera when dialog opens/closes or tab changes
  useEffect(() => {
    if (open && tab === 0) {
      // Small delay to let DOM render the div
      const t = setTimeout(startCamera, 150);
      return () => { clearTimeout(t); stopCamera(); };
    } else {
      stopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  // Clean up on unmount
  useEffect(() => () => { stopCamera(); }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    handledRef.current = false;
    try {
      const scanner = new Html5Qrcode('qr-file-region', { verbose: false });
      const result = await scanner.scanFile(file, true);
      const parsed = parseQrResult(result);
      if (parsed) {
        await acceptResult(parsed);
      } else {
        setError('Not a valid Voyage Santé+ QR code. Scan the QR from the health pass.');
      }
    } catch {
      setError('Could not read QR code from image. Try a clearer photo.');
    }
    // reset so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { stopCamera(); setError(null); handledRef.current = false; onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
        Scan Health Pass QR
        <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
        <Tab icon={<CameraAltIcon fontSize="small" />} iconPosition="start" label="Camera" value={0} sx={{ minHeight: 40 }} />
        <Tab icon={<UploadFileIcon fontSize="small" />} iconPosition="start" label="Upload Image" value={1} sx={{ minHeight: 40 }} />
      </Tabs>

      <DialogContent sx={{ pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {checking && (
          <Alert severity="info" sx={{ mb: 1.5 }}>Checking pass validity…</Alert>
        )}

        {/* Camera tab */}
        {tab === 0 && (
          <Box>
            <Box
              id={SCANNER_ID}
              sx={{ width: '100%', minHeight: 280, bgcolor: '#000', borderRadius: 1, overflow: 'hidden' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              Point camera at the Voyage Santé+ QR code
            </Typography>
          </Box>
        )}

        {/* Upload tab */}
        {tab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            {/* hidden div required by html5-qrcode for file scanning */}
            <div id="qr-file-region" style={{ display: 'none' }} />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              variant="outlined"
              size="large"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={checking}
              sx={{ width: '100%' }}
            >
              Choose QR Code Image
            </Button>
            <Typography variant="caption" color="text.secondary">
              Select a screenshot or photo containing the QR code
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default QrScanner;
